import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { GoogleMap, MarkerF, InfoWindow, Circle } from '@react-google-maps/api';
import { FaDirections, FaMapMarkerAlt } from 'react-icons/fa';
import { MdMyLocation } from 'react-icons/md';
import styles from './index.module.scss';
import axios from 'axios';

const GoogleMapProductLocation = ({ latitude, longitude, address, district, state }) => {
  const [map, setMap] = useState(null);
  const [infoWindowOpen, setInfoWindowOpen] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);

  // Check if Google Maps is already loaded

  useEffect(() => {
    console.log('🔍 Checking Google Maps:', {
      windowGoogle: !!window.google,
      googleMaps: !!window.google?.maps,
      scriptTag: document.querySelector('script[src*="maps.googleapis.com"]')
    });

  }, []);
  useEffect(() => {
    const checkGoogleMaps = () => {
      if (window.google && window.google.maps) {
        setIsLoaded(true);
      } else {
        // Wait a bit and check again
        setTimeout(checkGoogleMaps, 100);
      }
    };
    checkGoogleMaps();
  }, []);

  const mapOptions = useMemo(() => ({
    mapTypeControl: true,
    streetViewControl: true,
    fullscreenControl: true,
    zoomControl: true,
    mapTypeId: 'roadmap',
    disableDefaultUI: false,
  }), []);

  const containerStyle = useMemo(() => ({
    width: '100%',
    height: '100%'
  }), []);

  const onLoad = useCallback((mapInstance) => {
    console.log('✅ Map loaded successfully');
    setMap(mapInstance);
    setTimeout(() => setInfoWindowOpen(true), 1000);
  }, []);

  const onUnmount = useCallback(() => {
    console.log('🧹 Map unmounting');
    setMap(null);
  }, []);

  const getUserLocation = useCallback(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
        },
        (error) => {
          console.error('Error getting location:', error);
        }
      );
    }
  }, []);

  const openDirections = useCallback(() => {
    const destination = `${latitude},${longitude}`;
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    const googleMapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${destination}&travelmode=driving`;
    window.open(googleMapsUrl, '_blank');
  }, [latitude, longitude]);

  const centerToUserLocation = useCallback(() => {
    if (userLocation && map) {
      map.panTo(userLocation);
      map.setZoom(15);
    } else {
      getUserLocation();
    }
  }, [userLocation, map, getUserLocation]);

  // Validate coordinates
  if (!latitude || !longitude || isNaN(latitude) || isNaN(longitude)) {
    return (
      <div className={styles.mapWrapper}>
        <div className={styles.errorBox}>
          <div className={styles.errorIcon}>📍</div>
          <p className={styles.errorText}>Location Not Available</p>
          <span className={styles.errorSubtext}>Coordinates are missing or invalid</span>
        </div>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className={styles.mapWrapper}>
        <div className={styles.mapHeader}>
          <div className={styles.headerContent}>
            <div className={styles.headerLeft}>
              <FaMapMarkerAlt className={styles.headerIcon} />
              <div>
                <h3>Product Location</h3>
                <p>{district}{state ? `, ${state}` : ''}</p>
              </div>
            </div>
          </div>
        </div>
        <div className={styles.mapContainer}>
          <div className={styles.loading}>
            <div className={styles.spinner}></div>
            <p>Loading map...</p>
          </div>
        </div>
      </div>
    );
  }

  const center = {
    lat: Number(latitude),
    lng: Number(longitude)
  };

  const circleOptions = {
    strokeColor: '#046BD2',
    strokeOpacity: 0.3,
    strokeWeight: 2,
    fillColor: '#046BD2',
    fillOpacity: 0.1,
    clickable: false,
    draggable: false,
    editable: false,
    visible: true,
    radius: 500,
    zIndex: 1
  };

  return (
    <div className={styles.mapWrapper}>
      <div className={styles.mapHeader}>
        <div className={styles.headerContent}>
          <div className={styles.headerLeft}>
            <FaMapMarkerAlt className={styles.headerIcon} />
            <div>
              <h3>Product Location</h3>
              <p>{district}{state ? `, ${state}` : ''}</p>
            </div>
          </div>
          <button onClick={openDirections} className={styles.directionsButton} title="Get Directions">
            <FaDirections />
            <span>Directions</span>
          </button>
        </div>
      </div>

      <div className={styles.mapContainer}>
        <GoogleMap
          mapContainerStyle={containerStyle}
          center={center}
          zoom={15}
          onLoad={onLoad}
          onUnmount={onUnmount}
          options={mapOptions}
        >
          <Circle center={center} options={circleOptions} />

          <MarkerF
            position={center}
            onClick={() => setInfoWindowOpen(!infoWindowOpen)}
            title={district || 'Product Location'}
            animation={window.google?.maps?.Animation?.DROP}
            icon={"https://maps.google.com/mapfiles/ms/icons/red-dot.png"}
          >
            {/* {infoWindowOpen && (
              <InfoWindow
                onCloseClick={() => setInfoWindowOpen(false)}
                options={{
                  pixelOffset: new window.google.maps.Size(0, -40)
                }}
              >
                <div className={styles.infoWindow}>
                  <h4>📍 Product Location</h4>
                  <p className={styles.infoLocation}>
                    {district || 'Location'}
                    {state ? `, ${state}` : ''}
                  </p>
                  {address && <p className={styles.infoAddress}>{address}</p>}
                  <button onClick={openDirections} className={styles.infoDirectionsBtn}>
                    <FaDirections /> Get Directions
                  </button>
                  <p className={styles.infoDisclaimer}>
                    📌 Approximate location for privacy
                  </p>
                </div>
              </InfoWindow>
            )} */}
          </MarkerF>

          {userLocation && (
            <MarkerF
              position={userLocation}
              icon={{
                path: window.google.maps.SymbolPath.CIRCLE,
                scale: 8,
                fillColor: '#4285F4',
                fillOpacity: 1,
                strokeColor: '#FFFFFF',
                strokeWeight: 2,
              }}
              title="Your Location"
            />
          )}
        </GoogleMap>

        <div className={styles.mapControls}>
          <button
            onClick={centerToUserLocation}
            className={styles.controlButton}
            title="Center to my location"
          >
            <MdMyLocation />
          </button>
          <button
            onClick={openDirections}
            className={styles.controlButton}
            title="Get directions"
          >
            <FaDirections />
          </button>
        </div>
      </div>

      <div className={styles.mapFooter}>
        <div className={styles.footerInfo}>
          <div className={styles.footerItem}>
            <span className={styles.footerIcon}>🌐</span>
            <span className={styles.footerText}>
              {Number(latitude).toFixed(6)}, {Number(longitude).toFixed(6)}
            </span>
          </div>
          <div className={styles.footerItem}>
            <span className={styles.footerIcon}>📏</span>
            <span className={styles.footerText}>~500m radius</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GoogleMapProductLocation;